import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MANIFESTO } from "../data/events";

const EASE = [0.76, 0, 0.24, 1];

export const Manifesto = () => {
  return (
    <section
      id="manifesto"
      data-testid="manifesto-section"
      className="relative bg-[color:var(--bt-bg-2)] border-y border-[color:var(--bt-gold-line)]"
    >
      {/* Kicker */}
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 pt-24 md:pt-40 pb-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="grid md:grid-cols-12 gap-6 items-end"
        >
          <div className="md:col-span-8">
            <div className="flex items-center gap-4 mb-6">
              <div className="h-px w-10 bg-[color:var(--bt-gold)]" />
              <span
                data-testid="manifesto-eyebrow"
                className="text-[11px] tracking-widest-brand uppercase text-[color:var(--bt-gold)]"
              >
                Chapter III · Manifesto
              </span>
            </div>
            <h2
              data-testid="manifesto-heading"
              className="font-serif-display text-[9vw] md:text-[5.5vw] leading-[0.95] tracking-tight text-[color:var(--bt-ink)]"
            >
              Why{" "}
              <span className="italic font-light text-[color:var(--bt-gold)]">
                BlackTieDo.
              </span>
            </h2>
          </div>
        </motion.div>
      </div>

      {/* Chapters */}
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 pb-24 md:pb-32">
        {MANIFESTO.map((chapter, i) => (
          <Chapter key={chapter.number} chapter={chapter} index={i} />
        ))}
      </div>
    </section>
  );
};

const Chapter = ({ chapter, index }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  const reverse = index % 2 === 1;

  return (
    <div
      ref={ref}
      data-testid={`manifesto-chapter-${chapter.number}`}
      className="grid md:grid-cols-12 gap-6 md:gap-12 py-16 md:py-28 border-t border-[color:var(--bt-gold-line)]"
    >
      {/* Number */}
      <motion.div
        initial={{ opacity: 0, x: reverse ? 20 : -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE }}
        className={`md:col-span-2 ${
          reverse ? "md:order-3 md:text-right" : ""
        }`}
      >
        <div className="md:sticky md:top-32">
          <div className="font-serif-display italic text-[color:var(--bt-gold)] text-[18vw] md:text-[10vw] leading-none">
            {chapter.number}
          </div>
          <div className="mt-4 text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)]">
            - {chapter.kicker}
          </div>
        </div>
      </motion.div>

      {/* Image */}
      <div
        className={`md:col-span-5 ${
          reverse ? "md:order-1" : "md:order-2"
        }`}
      >
        <div className="relative overflow-hidden h-[60vh] md:h-[80vh] border border-[color:var(--bt-gold-line)] spotlight-frame">
          <motion.img
            style={{ y: imgY }}
            src={chapter.image}
            alt={chapter.title}
            className="absolute inset-0 h-[115%] w-full object-cover will-change-transform"
          />
          <div className="absolute inset-0 bg-[color:var(--bt-bg)]/25" />
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <span className="w-1.5 h-1.5 bg-[color:var(--bt-gold)]" />
            <span className="text-[9px] tracking-widest-brand uppercase text-[color:var(--bt-ink-dim)]">
              Plate {chapter.number}
            </span>
          </div>
        </div>
      </div>

      {/* Copy */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
        className={`md:col-span-5 flex flex-col justify-center ${
          reverse ? "md:order-2" : "md:order-3"
        }`}
      >
        <h3 className="font-serif-display text-4xl md:text-6xl leading-[1] tracking-tight text-[color:var(--bt-ink)]">
          {chapter.title}
        </h3>
        <p className="mt-6 md:mt-8 text-[color:var(--bt-ink-dim)] text-base md:text-lg leading-relaxed max-w-lg">
          {chapter.body}
        </p>
        <div className="mt-8 flex items-center gap-3">
          <div className="h-px w-10 bg-[color:var(--bt-gold)]" />
          <span className="text-[10px] tracking-widest-brand uppercase text-[color:var(--bt-gold)]">
            Continue reading
          </span>
        </div>
      </motion.div>
    </div>
  );
};

export default Manifesto;
